import { EventListFilter } from '@shared/enums/EventListFilter';

export const EVENT_CENTER_VIEWS = Object.freeze({
	CURRENT:      'current',
	UPCOMING:     'upcoming',
	REPLAYS:      'replays',
	COMPETITIONS: 'competitions',
});

export const CHRONOLOGICAL_SUBVIEW_KEY = 'chronological';
export const ALL_EVENTS_PER_PAGE = 50;

export const FILTER_KEYS = Object.freeze([
	'q',
	EventListFilter.TEAM,
	EventListFilter.CLUB,
	EventListFilter.LEAGUE,
	EventListFilter.SPORT,
	EventListFilter.TEAM_CATEGORY,
	EventListFilter.EVENT_TOPIC,
]);

const PAGINATED_VIEWS = new Set([EVENT_CENTER_VIEWS.UPCOMING, EVENT_CENTER_VIEWS.REPLAYS]);
const ALL_EVENTS_VIEWS = new Set([EVENT_CENTER_VIEWS.CURRENT, EVENT_CENTER_VIEWS.UPCOMING, EVENT_CENTER_VIEWS.REPLAYS]);

export const createDefaultQueryState = () => ({
	view:                            EVENT_CENTER_VIEWS.CURRENT,
	competition:                     null,
	subview:                         null,
	substage:                        null,
	page:                            null,
	q:                               '',
	[EventListFilter.TEAM]:          null,
	[EventListFilter.CLUB]:          null,
	[EventListFilter.LEAGUE]:        null,
	[EventListFilter.SPORT]:         null,
	[EventListFilter.TEAM_CATEGORY]: null,
	[EventListFilter.EVENT_TOPIC]:   null,
});

export const getEnabledCompetitions = (competitions = []) => competitions.filter(competition => competition.enabled);

export const getVisibilityMode = (contextConfig, competitions = []) => {
	const enabledCompetitions = getEnabledCompetitions(competitions);
	const hasEnabledCompetitions = enabledCompetitions.length > 0;

	if (!hasEnabledCompetitions) {
		return {
			hasEnabledCompetitions,
			showAllEventsTab:     true,
			showCompetitionsTab:  false,
			forceCompetitionMode: false,
		};
	}

	if (contextConfig.showAllEvents) {
		return {
			hasEnabledCompetitions,
			showAllEventsTab:     true,
			showCompetitionsTab:  true,
			forceCompetitionMode: false,
		};
	}

	return {
		hasEnabledCompetitions,
		showAllEventsTab:     false,
		showCompetitionsTab:  false,
		forceCompetitionMode: true,
	};
};

const getDefaultCompetition = (competitions = []) => {
	const enabledCompetitions = getEnabledCompetitions(competitions);
	return enabledCompetitions.find(competition => competition.isActive) || enabledCompetitions[0] || null;
};

export const resolveDefaultView = ({ contextConfig, competitions = [], feedState = null }) => {
	const defaultCompetition = getDefaultCompetition(competitions);
	const visibilityMode = getVisibilityMode(contextConfig, competitions);
	const hasCurrentFeed = Array.isArray(feedState?.current?.events);
	const currentEvents = hasCurrentFeed ? feedState.current.events : [];

	if (visibilityMode.forceCompetitionMode && defaultCompetition) {
		return {
			view:        EVENT_CENTER_VIEWS.COMPETITIONS,
			competition: String(defaultCompetition.id),
		};
	}

	if (contextConfig.isLeagueContext && defaultCompetition) {
		return {
			view:        EVENT_CENTER_VIEWS.COMPETITIONS,
			competition: String(defaultCompetition.id),
		};
	}

	if (!hasCurrentFeed) {
		return {
			view:        EVENT_CENTER_VIEWS.CURRENT,
			competition: null,
		};
	}

	return {
		view:        currentEvents.length > 0 ? EVENT_CENTER_VIEWS.CURRENT : EVENT_CENTER_VIEWS.UPCOMING,
		competition: null,
	};
};

const asTrimmedString = (value) => {
	if (value === null || typeof value === 'undefined') {
		return null;
	}

	const normalized = String(value).trim();
	return normalized.length > 0 ? normalized : null;
};

const asPositiveInt = (value) => {
	const parsed = Number.parseInt(value, 10);
	if (!Number.isFinite(parsed) || parsed <= 0) {
		return null;
	}

	return parsed;
};

const getCompetitionById = (competitions = [], competitionId) => {
	if (!competitionId) {
		return null;
	}

	return competitions.find(competition => String(competition.id) === String(competitionId)) || null;
};

const getAllowedSubviewKeys = (competition = null) => {
	if (!competition) {
		return [CHRONOLOGICAL_SUBVIEW_KEY];
	}

	const stageKeys = (competition.stages || []).map(stage => String(stage.id));
	return [CHRONOLOGICAL_SUBVIEW_KEY, ...stageKeys];
};

const getStageById = (competition = null, stageId = null) => {
	if (!competition || !stageId) {
		return null;
	}

	return (competition.stages || []).find(stage => String(stage.id) === String(stageId)) || null;
};

const objectFromParams = (params) => {
	if (!params) {
		return {};
	}

	if (params instanceof URLSearchParams) {
		return Object.fromEntries(params.entries());
	}

	return { ...params };
};

export const sanitizeQueryState = ({ rawQuery, contextConfig, competitions, fallbackState }) => {
	const input = objectFromParams(rawQuery);
	const defaultState = {
		...createDefaultQueryState(),
		...fallbackState,
	};

	const output = {
		...defaultState,
	};

	const droppedKeys = [];
	const visibility = getVisibilityMode(contextConfig, competitions);
	const defaultCompetition = getDefaultCompetition(competitions);
	const requestedView = asTrimmedString(input.view);

	const isRequestedViewValid = requestedView && Object.values(EVENT_CENTER_VIEWS).includes(requestedView);
	if (!isRequestedViewValid) {
		droppedKeys.push('view');
	}

	const candidateView = isRequestedViewValid ? requestedView : defaultState.view;

	const requestedCompetition = asTrimmedString(input.competition);
	const selectedCompetition = getCompetitionById(competitions, requestedCompetition);
	const isCompetitionValid = !!selectedCompetition && selectedCompetition.enabled;

	let view = candidateView;
	const competitionInvalidInCompetitionView = view === EVENT_CENTER_VIEWS.COMPETITIONS && !isCompetitionValid;
	if (competitionInvalidInCompetitionView) {
		view = defaultState.view;
		droppedKeys.push('competition');
	}

	if (visibility.forceCompetitionMode && defaultCompetition) {
		view = EVENT_CENTER_VIEWS.COMPETITIONS;
	}

	if (view === EVENT_CENTER_VIEWS.COMPETITIONS && !visibility.hasEnabledCompetitions) {
		view = defaultState.view;
		droppedKeys.push('view');
	}

	output.view = view;

	if (view === EVENT_CENTER_VIEWS.COMPETITIONS) {
		const effectiveCompetition = isCompetitionValid ? selectedCompetition : defaultCompetition;
		output.competition = effectiveCompetition ? String(effectiveCompetition.id) : null;

		const allowedSubviews = getAllowedSubviewKeys(effectiveCompetition);
		const requestedSubview = asTrimmedString(input.subview);
		const isSubviewValid = requestedSubview && allowedSubviews.includes(requestedSubview);
		const subview = isSubviewValid
			? requestedSubview
			: (effectiveCompetition?.stages?.[0]?.id ? String(effectiveCompetition.stages[0].id) : CHRONOLOGICAL_SUBVIEW_KEY);

		if (requestedSubview && !isSubviewValid) {
			droppedKeys.push('subview');
		}
		output.subview = subview;

		if (subview && subview !== CHRONOLOGICAL_SUBVIEW_KEY) {
			const stage = getStageById(effectiveCompetition, subview);
			const subStages = stage?.subStages || [];
			const requestedSubStage = asTrimmedString(input.substage);
			const hasSelectableSubStages = subStages.length > 0;
			const isSubStageValid = hasSelectableSubStages && subStages.some(subStage => String(subStage.id) === requestedSubStage);

			if (hasSelectableSubStages && isSubStageValid) {
				output.substage = requestedSubStage;
			} else {
				output.substage = null;
				if (requestedSubStage) {
					droppedKeys.push('substage');
				}
			}
		} else {
			output.substage = null;
			if (input.substage) {
				droppedKeys.push('substage');
			}
		}

		output.page = null;
		if (input.page) {
			droppedKeys.push('page');
		}
	} else {
		output.competition = null;
		output.subview = null;
		output.substage = null;
		if (input.competition) {
			droppedKeys.push('competition');
		}
		if (input.subview) {
			droppedKeys.push('subview');
		}
		if (input.substage) {
			droppedKeys.push('substage');
		}

		if (PAGINATED_VIEWS.has(view)) {
			output.page = asPositiveInt(input.page) || 1;
			if (input.page && !asPositiveInt(input.page)) {
				droppedKeys.push('page');
			}
		} else {
			output.page = null;
			if (input.page) {
				droppedKeys.push('page');
			}
		}
	}

	for (const filterKey of FILTER_KEYS) {
		const value = asTrimmedString(input[filterKey]);
		if (filterKey === 'q') {
			output[filterKey] = value || '';
			continue;
		}
		output[filterKey] = value;
	}

	if (view === EVENT_CENTER_VIEWS.COMPETITIONS) {
		output.club = null;
		output.league = null;
		output.sport = null;
	}

	if (!ALL_EVENTS_VIEWS.has(view) && view !== EVENT_CENTER_VIEWS.COMPETITIONS) {
		output.view = defaultState.view;
	}

	return {
		state: output,
		droppedKeys,
	};
};

export const shouldResetPagination = (previousState, nextState) => {
	if (!previousState || !nextState) {
		return true;
	}

	if (previousState.view !== nextState.view) {
		return true;
	}

	for (const key of ['competition', 'subview', 'substage', ...FILTER_KEYS]) {
		if (previousState[key] !== nextState[key]) {
			return true;
		}
	}

	return false;
};

const sortByStartAscending = (a, b) => a.startTimestamp - b.startTimestamp;
const sortByStartDescending = (a, b) => b.startTimestamp - a.startTimestamp;

const getParticipantValues = (event, mapParticipant) => (event.participants || [])
	.map(participant => mapParticipant(participant?.participatable))
	.filter(Boolean);

export const getEventFieldValues = (event, field) => {
	switch (field) {
	case EventListFilter.TEAM:
		return getParticipantValues(event, participatable => participatable?.id)
			.concat(event.teamId ? [event.teamId] : [])
			.concat(event.teamHome?.id ? [event.teamHome.id] : [])
			.concat(event.teamGuest?.id ? [event.teamGuest.id] : []);
	case EventListFilter.CLUB:
		return getParticipantValues(event, participatable => participatable?.club?.id)
			.concat(event.clubId ? [event.clubId] : [])
			.concat(event.teamHome?.club?.id ? [event.teamHome.club.id] : [])
			.concat(event.teamGuest?.club?.id ? [event.teamGuest.club.id] : []);
	case EventListFilter.LEAGUE: {
		const leagueValue = event.leagueId || event.league?.id || null;
		return leagueValue ? [leagueValue] : [];
	}
	case EventListFilter.SPORT: {
		const sportValue = event.sportId || event.sport?.id || null;
		return sportValue ? [sportValue] : [];
	}
	case 'category': {
		const categoryValue = event.categoryParent || event.category || null;
		return categoryValue ? [categoryValue] : [];
	}
	case EventListFilter.TEAM_CATEGORY:
		return getParticipantValues(event, participatable => participatable?.category)
			.concat(event.teamCategory ? [event.teamCategory] : [])
			.concat(event.team?.category ? [event.team.category] : [])
			.concat(event.teamHome?.category ? [event.teamHome.category] : [])
			.concat(event.teamGuest?.category ? [event.teamGuest.category] : []);
	case EventListFilter.EVENT_TOPIC: {
		const eventTopicValue = event.eventTopicId || null;
		return eventTopicValue ? [eventTopicValue] : [];
	}
	default: {
		const value = event[field] ?? null;
		return value ? [value] : [];
	}
	}
};

const getEventTextSearchValues = (event, field) => {
	switch (field) {
	case EventListFilter.TEAM:
		return getParticipantValues(event, participatable => participatable?.name)
			.concat(event.team ? [event.team] : [])
			.concat(event.teamHome?.name ? [event.teamHome.name] : [])
			.concat(event.teamGuest?.name ? [event.teamGuest.name] : []);
	case EventListFilter.CLUB:
		return getParticipantValues(event, participatable => participatable?.club?.name)
			.concat(event.club ? [event.club] : [])
			.concat(event.teamHome?.club?.name ? [event.teamHome.club.name] : [])
			.concat(event.teamGuest?.club?.name ? [event.teamGuest.club.name] : []);
	case EventListFilter.LEAGUE: {
		const leagueValue = typeof event.league === 'string' ? event.league : (event.league?.name || null);
		return leagueValue ? [leagueValue] : [];
	}
	case EventListFilter.SPORT: {
		const sportValue = typeof event.sport === 'string' ? event.sport : (event.sport?.name || null);
		return sportValue ? [sportValue] : [];
	}
	case EventListFilter.TEAM_CATEGORY:
		return getEventFieldValues(event, field);
	case EventListFilter.EVENT_TOPIC: {
		const eventTopicValue = event.eventTopic || null;
		return eventTopicValue ? [eventTopicValue] : [];
	}
	default:
		return [];
	}
};

export const getEventFieldValue = (event, field) => {
	return getEventFieldValues(event, field)[0] ?? null;
};

const matchTextFilter = (event, query) => {
	if (!query) {
		return true;
	}

	const haystack = [
		event.title,
		...getEventTextSearchValues(event, EventListFilter.TEAM),
		...getEventTextSearchValues(event, EventListFilter.CLUB),
		...getEventTextSearchValues(event, EventListFilter.LEAGUE),
		...getEventTextSearchValues(event, EventListFilter.SPORT),
		...getEventTextSearchValues(event, EventListFilter.TEAM_CATEGORY),
		...getEventTextSearchValues(event, EventListFilter.EVENT_TOPIC),
	]
		.filter(Boolean)
		.join(' ')
		.toLowerCase();
	return haystack.includes(query.toLowerCase());
};

const matchFieldFilter = (event, field, value) => !value || getEventFieldValues(event, field).some(fieldValue => String(fieldValue) === String(value));

const applyCommonFilters = (events, queryState, allowedKeys) => {
	return events.filter(event => {
		if (!matchTextFilter(event, queryState.q)) {
			return false;
		}

		return allowedKeys.every(key => matchFieldFilter(event, key, queryState[key]));
	});
};

export const selectViewEvents = ({ events = [], queryState, nowTimestamp }) => {
	const now = nowTimestamp || Math.floor(Date.now() / 1000);
	const currentWindowStart = now - (2 * 24 * 60 * 60);
	const currentWindowEnd = now + (7 * 24 * 60 * 60);

	if (queryState.view === EVENT_CENTER_VIEWS.COMPETITIONS) {
		const filteredByCompetition = events.filter(event => String(event.competitionId ?? event.competition_id) === String(queryState.competition));
		let filteredBySubview = filteredByCompetition;

		if (queryState.subview && queryState.subview !== CHRONOLOGICAL_SUBVIEW_KEY) {
			filteredBySubview = filteredBySubview.filter(event => String(event.stageGroupId ?? event.stage_group_id) === String(queryState.subview));
			if (queryState.substage) {
				filteredBySubview = filteredBySubview.filter(event => String(event.subStageId ?? event.sub_stage_id) === String(queryState.substage));
			}
		}

		const filtered = applyCommonFilters(filteredBySubview, queryState, [EventListFilter.TEAM, EventListFilter.TEAM_CATEGORY]);
		const sorted = [...filtered].sort(sortByStartAscending);
		return {
			events:     sorted,
			pagination: null,
		};
	}

	const filteredAllEvents = applyCommonFilters(events, queryState, [
		EventListFilter.TEAM,
		EventListFilter.CLUB,
		EventListFilter.LEAGUE,
		EventListFilter.SPORT,
		EventListFilter.TEAM_CATEGORY,
		EventListFilter.EVENT_TOPIC,
	]);

	if (queryState.view === EVENT_CENTER_VIEWS.CURRENT) {
		return {
			events: filteredAllEvents
				.filter(event => event.startTimestamp >= currentWindowStart && event.startTimestamp <= currentWindowEnd)
				.sort(sortByStartAscending),
			pagination: null,
		};
	}

	if (queryState.view === EVENT_CENTER_VIEWS.UPCOMING) {
		return paginateEvents(
			filteredAllEvents.filter(event => event.startTimestamp > now).sort(sortByStartAscending),
			queryState.page || 1,
			ALL_EVENTS_PER_PAGE
		);
	}

	if (queryState.view === EVENT_CENTER_VIEWS.REPLAYS) {
		return paginateEvents(
			filteredAllEvents.filter(event => event.startTimestamp < now).sort(sortByStartDescending),
			queryState.page || 1,
			ALL_EVENTS_PER_PAGE
		);
	}

	return {
		events:     [],
		pagination: null,
	};
};

export const paginateEvents = (events = [], currentPage = 1, perPage = 8) => {
	const page = asPositiveInt(currentPage) || 1;
	const safePerPage = asPositiveInt(perPage) || 8;
	const total = events.length;
	const lastPage = Math.max(Math.ceil(total / safePerPage), 1);
	const offset = (Math.min(page, lastPage) - 1) * safePerPage;

	return {
		events:     events.slice(offset, offset + safePerPage),
		pagination: {
			currentPage: Math.min(page, lastPage),
			lastPage,
			perPage:     safePerPage,
			total,
		},
	};
};

export const toURLParams = (queryState) => {
	const params = new URLSearchParams();

	for (const [key, value] of Object.entries(queryState)) {
		if (value === null || typeof value === 'undefined' || value === '') {
			continue;
		}
		params.set(key, String(value));
	}

	return params;
};
